import { isDefined } from "@clipboard-health/util-ts";
import { isBefore, subMinutes } from "date-fns";

export interface ValidateClockInTimestampParams {
  selectedTime: Date;
  geofenceArrivalTime?: Date;
  shiftStartAt: Date;
  facilitySettings: {
    preventEarlyClockIns: boolean;
    earlyClockInMinutes?: number;
  };
  workerClockIn?: Date;
  clockOutTime?: Date;
  breakStartTime?: Date;
  isGeofenceFacility?: boolean;
  isNfcOrMeshFacility?: boolean;
}

export type ClockInErrorType =
  | "GEOFENCE_ERROR"
  | "WAY_TOO_EARLY"
  | "TOO_EARLY_10_MIN"
  | "NO_EARLY_CLOCK_IN"
  | "AFTER_CLOCK_OUT_OR_BREAK";

export type ClockInWarningType = "MISSED_PUNCH_WARNING";

export interface ClockInValidationResult {
  isValid: boolean;
  errorType?: ClockInErrorType;
  warningType?: ClockInWarningType;
  message?: string;
}

const WAY_TOO_EARLY_THRESHOLD_MINUTES = 30;

/**
 * Validates clock-in timestamp against business rules
 *
 * Returns error states that block submission or warning states that require
 * missed punch reason entry. Used across Missed Clock In flow, timesheet submission,
 * and clock-in time editing.
 *
 * @param params - Validation parameters
 * @returns Validation result with isValid flag and optional error/warning
 */
export function validateClockInTimestamp(
  params: ValidateClockInTimestampParams
): ClockInValidationResult {
  const {
    selectedTime,
    geofenceArrivalTime,
    shiftStartAt,
    facilitySettings,
    workerClockIn,
    clockOutTime,
    breakStartTime,
    isGeofenceFacility = false,
    isNfcOrMeshFacility = false,
  } = params;

  // Priority 1: Geofence error (highest priority)
  const geofenceError = checkGeofenceError(selectedTime, geofenceArrivalTime);
  if (geofenceError) {
    return geofenceError;
  }

  // Priority 2: Way too early (30 or more minutes before shift start)
  const wayTooEarlyError = checkWayTooEarlyError(selectedTime, shiftStartAt);
  if (wayTooEarlyError) {
    return wayTooEarlyError;
  }

  // Priority 3: Too early when facility doesn't prevent early clock-ins
  if (!facilitySettings.preventEarlyClockIns) {
    const tooEarlyError = checkTooEarlyError(
      selectedTime,
      shiftStartAt,
      facilitySettings.earlyClockInMinutes
    );
    if (tooEarlyError) {
      return tooEarlyError;
    }
  }

  // Priority 4: No early clock-in allowed when facility prevents early clock-ins
  if (facilitySettings.preventEarlyClockIns) {
    const noEarlyClockInError = checkNoEarlyClockInError(selectedTime, shiftStartAt);
    if (noEarlyClockInError) {
      return noEarlyClockInError;
    }
  }

  // Priority 5: After clock-out or break start
  const afterClockOutOrBreakError = checkAfterClockOutOrBreakError(
    selectedTime,
    clockOutTime,
    breakStartTime
  );
  if (afterClockOutOrBreakError) {
    return afterClockOutOrBreakError;
  }

  // Check for missed punch warning (blue warning, not blocking)
  const missedPunchWarning = checkMissedPunchWarning(
    selectedTime,
    workerClockIn,
    isGeofenceFacility,
    isNfcOrMeshFacility
  );
  if (missedPunchWarning) {
    return missedPunchWarning;
  }

  // No errors or warnings - validation passed
  return {
    isValid: true,
  };
}

/**
 * Error 1: Selected time is before geofence arrival
 */
function checkGeofenceError(
  selectedTime: Date,
  geofenceArrivalTime?: Date
): ClockInValidationResult | undefined {
  if (!isDefined(geofenceArrivalTime)) {
    return undefined;
  }

  if (isBefore(selectedTime, geofenceArrivalTime)) {
    return {
      isValid: false,
      errorType: "GEOFENCE_ERROR",
      message:
        "Clock-in time cannot be set before your recorded arrival, based on location tracking.",
    };
  }

  return undefined;
}

/**
 * Error 2: 30 or more minutes before shift start (no geofence error)
 */
function checkWayTooEarlyError(
  selectedTime: Date,
  shiftStartAt: Date
): ClockInValidationResult | undefined {
  const wayTooEarlyThreshold = subMinutes(shiftStartAt, WAY_TOO_EARLY_THRESHOLD_MINUTES);

  // Check if selected time is before OR equal to the threshold (30+ minutes early)
  if (
    isBefore(selectedTime, wayTooEarlyThreshold) ||
    selectedTime.getTime() === wayTooEarlyThreshold.getTime()
  ) {
    return {
      isValid: false,
      errorType: "WAY_TOO_EARLY",
      message:
        "If you are scheduled to work earlier, please ask the workplace to update the time in the app.",
    };
  }

  return undefined;
}

/**
 * Error 3: Earlier than facility's configured early clock-in threshold
 */
function checkTooEarlyError(
  selectedTime: Date,
  shiftStartAt: Date,
  earlyClockInMinutes?: number
): ClockInValidationResult | undefined {
  // If no early clock-in minutes configured, don't apply this check
  if (!isDefined(earlyClockInMinutes) || earlyClockInMinutes === 0) {
    return undefined;
  }

  const tooEarlyThreshold = subMinutes(shiftStartAt, earlyClockInMinutes);

  if (isBefore(selectedTime, tooEarlyThreshold)) {
    return {
      isValid: false,
      errorType: "TOO_EARLY_10_MIN",
      message: `Clock-in time cannot be set before ${earlyClockInMinutes} minutes before shift start.`,
    };
  }

  return undefined;
}

/**
 * Error 4: Any time before shift start when facility prevents early clock-ins
 */
function checkNoEarlyClockInError(
  selectedTime: Date,
  shiftStartAt: Date
): ClockInValidationResult | undefined {
  if (isBefore(selectedTime, shiftStartAt)) {
    return {
      isValid: false,
      errorType: "NO_EARLY_CLOCK_IN",
      message: "Clock-in time cannot be set before shift start.",
    };
  }

  return undefined;
}

/**
 * Error 5: Selected time is after clock-out or break start
 */
function checkAfterClockOutOrBreakError(
  selectedTime: Date,
  clockOutTime?: Date,
  breakStartTime?: Date
): ClockInValidationResult | undefined {
  if (isDefined(clockOutTime) && !isBefore(selectedTime, clockOutTime)) {
    return {
      isValid: false,
      errorType: "AFTER_CLOCK_OUT_OR_BREAK",
      message: "Clock-in time cannot be set after clock-out time.",
    };
  }

  if (isDefined(breakStartTime) && !isBefore(selectedTime, breakStartTime)) {
    return {
      isValid: false,
      errorType: "AFTER_CLOCK_OUT_OR_BREAK",
      message: "Clock-in time cannot be set after break start time.",
    };
  }

  return undefined;
}

/**
 * Blue warning: Missed punch warning (allows submission with reason)
 *
 * Conditions:
 * - No red error applies AND
 * - At geofence facility: workerClockIn not set OR selected time earlier than workerClockIn
 * - At NFC/Mesh facility: workerClockIn not set OR selected earlier
 */
function checkMissedPunchWarning(
  selectedTime: Date,
  workerClockIn: Date | undefined,
  isGeofenceFacility: boolean,
  isNfcOrMeshFacility: boolean
): ClockInValidationResult | undefined {
  // Only applies to facilities with timekeeping systems
  if (!isGeofenceFacility && !isNfcOrMeshFacility) {
    return undefined;
  }

  // Warning if workerClockIn is not set
  if (!isDefined(workerClockIn)) {
    return {
      isValid: true,
      warningType: "MISSED_PUNCH_WARNING",
      message:
        "You are recording a missed punch. No worries - you'll be able to add the reason on the next screen.",
    };
  }

  // Warning if selected time is earlier than workerClockIn
  if (isBefore(selectedTime, workerClockIn)) {
    return {
      isValid: true,
      warningType: "MISSED_PUNCH_WARNING",
      message:
        "You are recording a missed punch. No worries - you'll be able to add the reason on the next screen.",
    };
  }

  return undefined;
}
